// apps/web/src/pages/onboarding/[token]/review.tsx — Phase 7 plan 07-06.
//
// Onboarding step 4: final review + admin password capture (B3 fix).
// The page renders a read-only summary of the prior 3 steps plus
// two password inputs (adminPassword + adminPasswordConfirm).
// `Mutation.completeOnboarding` flips `Restaurant.isActive=true`
// AND sets `AdminUser.passwordHash` to `hashPassword(adminPassword)`
// in a single transaction (B3 fix — order matters because the
// operator must be able to log in immediately after). On success
// the page navigates to `/admin/login`; the user authenticates
// with the password they just chose.
//
// Per D-07-12 the validation is hybrid: the Zod check blocks the
// submit BEFORE the network call (12-char minimum + match), then
// the server re-validates with the same schema (Phase 2 password
// policy).

import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import { WizardShell } from "../../../components/WizardShell";
import { gqlRequest } from "../../../lib/api";

const REVIEW_QUERY = /* GraphQL */ `
  query OnboardingReviewContext {
    publicBookingContext {
      id
      slug
      name
      addressLine
      postalCode
      city
      capacityDefault
    }
  }
`;

const COMPLETE_ONBOARDING_MUTATION = /* GraphQL */ `
  mutation CompleteOnboarding($input: CompleteOnboardingInput!) {
    completeOnboarding(input: $input) {
      onboardingProgress
      restaurant {
        id
        slug
      }
    }
  }
`;

interface ReviewContext {
  publicBookingContext: {
    id: string;
    slug: string;
    name: string;
    addressLine: string | null;
    postalCode: string | null;
    city: string | null;
    capacityDefault: number;
  };
}

const MIN_PASSWORD_LENGTH = 12;

export function OnboardingReviewPage(): JSX.Element {
  const { t } = useTranslation();
  const [adminPassword, setAdminPassword] = useState("");
  const [adminPasswordConfirm, setAdminPasswordConfirm] = useState("");
  const [passwordError, setPasswordError] = useState<string | null>(null);

  const ctx = useQuery({
    queryKey: ["onboardingReviewContext"],
    queryFn: () => gqlRequest<ReviewContext>(REVIEW_QUERY),
    retry: false,
    refetchOnWindowFocus: false,
  });

  const mutation = useMutation({
    mutationFn: () =>
      gqlRequest(COMPLETE_ONBOARDING_MUTATION, {
        input: { adminPassword },
      }),
    onSuccess: () => {
      window.location.assign("/admin/login");
    },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (adminPassword.length < MIN_PASSWORD_LENGTH) {
      setPasswordError("signup.adminPasswordTooShort");
      return;
    }
    if (adminPassword !== adminPasswordConfirm) {
      setPasswordError("signup.adminPasswordMismatch");
      return;
    }
    setPasswordError(null);
    mutation.mutate();
  }

  const r = ctx.data?.publicBookingContext;

  return (
    <WizardShell currentStep="review">
      <form
        onSubmit={handleSubmit}
        className="flex flex-col gap-4"
        noValidate
        data-testid="onboarding-review-form"
      >
        <h1 className="font-heading text-2xl">{t("onboarding.reviewTitle")}</h1>
        <section
          className="border border-divider rounded-md p-3 flex flex-col gap-2 text-sm"
          data-testid="onboarding-review-summary"
        >
          <h2 className="font-heading text-base">
            {t("onboarding.reviewSummary")}
          </h2>
          {r ? (
            <dl className="grid grid-cols-[max-content_1fr] gap-x-3 gap-y-1">
              <dt className="text-text/70">Name</dt>
              <dd data-testid="review-name">{r.name ?? "—"}</dd>
              <dt className="text-text/70">Adresse</dt>
              <dd data-testid="review-address">
                {[r.addressLine, r.postalCode, r.city]
                  .filter((p) => Boolean(p && p.length > 0))
                  .join(", ") || "—"}
              </dd>
              <dt className="text-text/70">
                {t("onboarding.capacityTotalSeats")}
              </dt>
              <dd data-testid="review-capacity">{r.capacityDefault}</dd>
            </dl>
          ) : (
            <p className="text-text/60">{t("onboarding.submitting")}…</p>
          )}
        </section>
        <p className="text-sm text-text/70">
          {t("onboarding.adminPasswordHint")}
        </p>
        <label className="flex flex-col gap-1">
          <span className="font-heading text-sm">
            {t("signup.adminPassword")}
          </span>
          <input
            type="password"
            value={adminPassword}
            onChange={(e) => setAdminPassword(e.target.value)}
            className="field"
            autoComplete="new-password"
            minLength={MIN_PASSWORD_LENGTH}
            data-testid="onboarding-admin-password"
          />
          <span className="text-[11px] text-text/60">
            {t("signup.adminPasswordHint")}
          </span>
        </label>
        <label className="flex flex-col gap-1">
          <span className="font-heading text-sm">
            {t("signup.adminPasswordConfirm")}
          </span>
          <input
            type="password"
            value={adminPasswordConfirm}
            onChange={(e) => setAdminPasswordConfirm(e.target.value)}
            className="field"
            autoComplete="new-password"
            minLength={MIN_PASSWORD_LENGTH}
            data-testid="onboarding-admin-password-confirm"
          />
        </label>
        {passwordError ? (
          <span
            className="text-[11px] text-red-700"
            role="alert"
            data-testid="onboarding-password-error"
          >
            {t(passwordError)}
          </span>
        ) : null}
        <button
          type="submit"
          disabled={mutation.isPending}
          className="btn btn-primary"
          data-testid="onboarding-activate"
        >
          {mutation.isPending ? (
            <span className="inline-flex items-center gap-2">
              <Loader2 size={16} className="animate-spin" aria-hidden />
              {t("onboarding.submitting")}
            </span>
          ) : (
            t("onboarding.activate")
          )}
        </button>
      </form>
    </WizardShell>
  );
}
